import { create } from 'zustand'
import type { Card, SaveData, Statistics, OracleQuote, OracleStorage, MistakeEntry, GuestBookEntry, GuestBookTag, TeahouseMood, GameSettings, PerformancePoint, Danmaku } from '../types'
import { DeckManager } from '../engine/DeckManager'
import { ProgressTracker } from '../engine/ProgressTracker'
import { StatisticsEngine } from '../engine/StatisticsEngine'
import { MistakeBook } from '../engine/MistakeBook'
import { evaluateAchievements } from '../engine/Achievements'
import { isChapterUnlocked, getChapterCardIds, chapters } from '../data/chapters'
import { loadGame, saveGame, updateSettings as dbUpdateSettings } from '../storage/db'
import { playSuccessSound, playErrorSound } from '../utils/soundEngine'

const DUMMY_PERFORMANCE_DATA: PerformancePoint[] = [
  { trade: '第1场', pnl: 0, winRate: 50, benchmark: 0 },
  { trade: '第2场', pnl: 2.8, winRate: 60, benchmark: -0.5 },
  { trade: '第3场', pnl: -1.2, winRate: 50, benchmark: -1.8 },
  { trade: '第4场', pnl: 4.5, winRate: 66, benchmark: 0.2 },
  { trade: '第5场', pnl: 3.1, winRate: 60, benchmark: -0.8 },
  { trade: '第6场', pnl: 8.6, winRate: 71, benchmark: 1.1 },
  { trade: '第7场', pnl: 6.2, winRate: 68, benchmark: 0.5 },
  { trade: '第8场', pnl: 12.8, winRate: 75, benchmark: 2.3 },
  { trade: '第9场', pnl: 11.4, winRate: 73, benchmark: 1.9 },
  { trade: '第10场', pnl: 17.5, winRate: 80, benchmark: 3.2 },
]

interface GameState {
  // 引擎实例（内部管理）
  deckManager: DeckManager
  progressTracker: ProgressTracker
  statisticsEngine: StatisticsEngine
  mistakeBook: MistakeBook

  // 状态
  currentCard: Card | null
  isLoading: boolean
  isInitialized: boolean
  lastAnswerResult: {
    isCorrect: boolean
    feedback: string
  } | null

  // 设置
  settings: SaveData['settings']
  updateSettings: (partial: Partial<GameSettings>) => void
  toggleSound: () => void

  // 勋章
  unlockedAchievements: Record<string, number>

  // 答案之书
  oracleQuotes: OracleQuote[]
  oracleStorage: OracleStorage

  // 错题本 (扁平化视图, 给 UI 直接用)
  mistakeEntries: MistakeEntry[]

  // 留言板 (按时间倒序, 最新在前)
  guestBookEntries: GuestBookEntry[]

  // 上次学习位置
  lastStudyLocation?: { chapterId: number; sectionId: string }

  // 待弹出的勋章解锁 Toast (新解锁的勋章 ID 列表)
  pendingUnlockIds: string[]

  // 模拟交易战绩趋势数据 (给复盘页/Trading Performance 展示)
  performanceData: PerformancePoint[]

  // 解锁版本号（用于触发 UI 重新渲染）
  unlockVersion: number

  // 答题弹幕: 连对/连错触发, 飘字反馈
  danmaku: Danmaku[]                                  // 弹幕语料池
  correctStreak: number                               // 当前连对数（答错清零）
  wrongStreak: number                                 // 当前连错数（答对清零）
  pendingDanmaku: Danmaku | null                      // 待显示的弹幕（DanmakuLayer 消费）
  danmakuTick: number                                 // 单调递增, 强制 DanmakuLayer 重新读取 pendingDanmaku

  // 动作
  initialize: () => Promise<void>
  loadSection: (cards: Card[], shuffle?: boolean) => void
  nextCard: () => Card | null
  prevCard: () => Card | null
  submitAnswer: (cardId: string, choiceIndex: number, timeSpentMs: number) => void
  getProgress: () => { current: number; total: number }
  getStatistics: () => Statistics
  getBehaviorPatterns: () => { averageDecisionTimeMs: number; overallAccuracy: number; accuracyByType: Record<string, number>; decisionsByType: Record<string, number> }
  recordAchievementUnlock: (id: string) => void
  clearLastAnswer: () => void
  removeMistake: (cardId: string) => void
  recordStudyLocation: (chapterId: number, sectionId: string) => void
  consumePendingUnlocks: () => string[]  // 取出所有待弹的解锁 ID, 调用后清空
  unlockAllChapters: () => void          // 解锁全部章节（把所有卡片 mastery 设为 60）
  clearLearning: () => void              // 清空学习数据（进度/勋章/错题/统计/上次位置，保留 oracle/茶馆/设置）
  resetAll: () => void                   // 全部重置（在 clearLearning 基础上 + 清空 oracle/茶馆留言，保留 settings）

  // 答案之书 actions
  loadOracleQuotes: () => Promise<void>
  recordOracleDraw: (quoteId: string) => void
  toggleOracleFavorite: (quoteId: string) => void

  // 留言板 actions
  addGuestBookEntry: (content: string, tag: GuestBookTag, mood?: TeahouseMood) => void
  removeGuestBookEntry: (id: string) => void

  // 弹幕 actions
  loadDanmaku: () => Promise<void>
  consumePendingDanmaku: () => void
}

export const useGameStore = create<GameState>((set, get) => ({
  deckManager: new DeckManager(),
  progressTracker: new ProgressTracker(),
  statisticsEngine: new StatisticsEngine(),
  mistakeBook: new MistakeBook(),

  currentCard: null,
  isLoading: true,
  isInitialized: false,
  lastAnswerResult: null,
  unlockedAchievements: {},
  oracleQuotes: [],
  oracleStorage: { drawnIds: [], favorites: [] },
  mistakeEntries: [],
  guestBookEntries: [],
  lastStudyLocation: undefined,
  pendingUnlockIds: [],
  performanceData: DUMMY_PERFORMANCE_DATA,
  unlockVersion: 0,

  // 弹幕状态初始值
  danmaku: [],
  correctStreak: 0,
  wrongStreak: 0,
  pendingDanmaku: null,
  danmakuTick: 0,

  settings: {
    soundEnabled: true,
    animationSpeed: 'slow',
    fontSize: 'medium',
  },

  // 更新设置：同步更新内存状态 + 读-改-写持久化到 IndexedDB
  updateSettings: (partial) => {
    set(state => ({ settings: { ...state.settings, ...partial } }))
    void dbUpdateSettings(partial)
  },

  toggleSound: () => {
    const { settings, updateSettings } = get()
    updateSettings({ soundEnabled: !settings.soundEnabled })
  },

  initialize: async () => {
    try {
      const saveData = await loadGame()

      // 一次性迁移：v0.1 之前没有设置面板，旧存档里的 animationSpeed 不是用户选择，
      // 统一重置为「安静」并落库；localStorage 标记保证只执行一次，之后用户的修改不会被覆盖
      if (!localStorage.getItem('zg-anim-pref-migrated')) {
        saveData.settings = { ...saveData.settings, animationSpeed: 'slow' }
        localStorage.setItem('zg-anim-pref-migrated', '1')
        void dbUpdateSettings({ animationSpeed: 'slow' })
      }

      // 异步加载 oracle quotes (不阻塞初始化)
      import('../data/oracleLoader').then(async ({ loadOracleQuotes }) => {
        try {
          const quotes = await loadOracleQuotes()
          set({ oracleQuotes: quotes })
        } catch (err) {
          console.error('Failed to load oracle quotes:', err)
        }
      })

      // 异步加载弹幕语料 (不阻塞初始化, 弹幕只影响答题页, 失败时降级为空)
      import('../data/danmakuLoader').then(async ({ loadDanmaku }) => {
        try {
          const items = await loadDanmaku()
          set({ danmaku: items })
        } catch (err) {
          console.error('Failed to load danmaku:', err)
        }
      })

      set(state => {
        state.progressTracker.loadFromSave(saveData.progress)
        state.statisticsEngine.resetSession()
        return {
          settings: saveData.settings,
          unlockedAchievements: saveData.achievements
            ? Object.fromEntries(
                saveData.achievements
                  .filter(a => a.unlockedAt > 0)
                  .map(a => [a.id, a.unlockedAt])
              )
            : {},
          oracleStorage: saveData.oracle ?? { drawnIds: [], favorites: [] },
          lastStudyLocation: saveData.lastStudyLocation,
          // 留言板加载: 旧存档没有 guestBook, 用空数组; 按时间倒序保证最新在前
          guestBookEntries: [...(saveData.guestBook ?? [])].sort((a, b) => b.createdAt - a.createdAt),
          // 错题本加载: 旧存档没有 mistakeBook, 用空对象
          ...(function () {
            const mb = saveData.mistakeBook ?? {}
            state.mistakeBook.loadFromSave(mb)
            return { mistakeEntries: state.mistakeBook.getRanked() }
          })(),
          isLoading: false,
          isInitialized: true,
        }
      })
    } catch (error) {
      console.error('Failed to initialize game:', error)
      set({ isLoading: false, isInitialized: true })
    }
  },

  loadSection: (cards: Card[], shuffle = true) => {
    set(state => {
      state.deckManager.loadDeck(cards)
      if (shuffle) {
        state.deckManager.shuffle('random')
      }
      const firstCard = state.deckManager.drawNext()
      return { currentCard: firstCard, lastAnswerResult: null }
    })
  },

  nextCard: () => {
    const { deckManager } = get()
    const nextCard = deckManager.drawNext()
    set({ currentCard: nextCard, lastAnswerResult: null })
    return nextCard
  },

  prevCard: () => {
    const { deckManager } = get()
    const prevCard = deckManager.drawPrev()
    set({ currentCard: prevCard, lastAnswerResult: null })
    return prevCard
  },

  submitAnswer: (cardId: string, choiceIndex: number, timeSpentMs: number) => {
    const { deckManager, progressTracker, statisticsEngine, mistakeBook } = get()
    const result = deckManager.submitAnswer(cardId, choiceIndex)

    progressTracker.recordAttempt(cardId, result.isCorrect)

    // 播放答题像素音效
    if (result.isCorrect) {
      playSuccessSound()
    } else {
      playErrorSound()
    }

    // 需要获取卡片类型来记录统计
    const card = deckManager.getAllCards().find(c => c.id === cardId)
    if (card) {
      statisticsEngine.recordDecision(card.type, result.isCorrect, timeSpentMs)
    }

    // 错题本: 答错入册, 答对+mastery≥60 自动出册
    if (!result.isCorrect) {
      mistakeBook.recordMistake(cardId, choiceIndex)
    } else if (progressTracker.getMastery(cardId) >= 60) {
      mistakeBook.removeMistake(cardId)
    }

    // 勋章评估: 答完一题就 check, 看有没有新解锁的
    const behavior = get().statisticsEngine.getBehaviorPatterns()
    const stats = get().progressTracker.getStatistics()
    const chapterCardIds: Record<number, string[]> = {}
    for (let i = 1; i <= 5; i++) {
      chapterCardIds[i] = getChapterCardIds(i)
    }
    const progressMap = get().progressTracker.getAllProgress() as unknown as Record<string, { mastery: number; attempts: number; correctCount: number; lastAttempt: number }>
    const prevUnlocked = new Set(Object.keys(get().unlockedAchievements))
    const { newlyUnlocked } = evaluateAchievements(
      {
        progress: progressMap as never,
        totalAttempts: stats.totalAttempts,
        correctRate: stats.correctRate,
        averageDecisionTimeMs: behavior.averageDecisionTimeMs,
        chapterCardIds,
        isChapterUnlocked: (id: number) =>
          isChapterUnlocked(id, progressMap as never),
      },
      prevUnlocked
    )

    // 把新解锁的写入 store + 累计到 pendingUnlockIds 让 UI 弹 toast
    if (newlyUnlocked.length > 0) {
      const updated = { ...get().unlockedAchievements }
      for (const id of newlyUnlocked) updated[id] = Date.now()
      set({
        unlockedAchievements: updated,
        pendingUnlockIds: [...get().pendingUnlockIds, ...newlyUnlocked],
      })
    }

    // 异步保存 (全量覆盖, 必须带 oracle 否则答案之书数据丢失)
    const saveData = get().progressTracker.toSaveData()
    saveGame({
      progress: saveData,
      statistics: get().progressTracker.getStatistics(),
      settings: get().settings,
      achievements: Object.entries(get().unlockedAchievements).map(([id, unlockedAt]) => ({
        id,
        unlockedAt,
      })),
      mistakeBook: mistakeBook.toSaveData(),
      oracle: get().oracleStorage,
      lastStudyLocation: get().lastStudyLocation,
    }).catch(console.error)

    // 同步错题本到 UI (扁平化, 给页面用)
    set({
      mistakeEntries: mistakeBook.getRanked(),
      lastAnswerResult: {
        isCorrect: result.isCorrect,
        feedback: result.feedback,
      },
    })

    // 弹幕触发: 根据 streak 决定是否推送一条 Z 哥口吻反馈
    // - 连对 3 题 → praise (短小表扬)
    // - 连对 5 题 → hot (火爆庆祝, 含算账句)
    // - 连错 2 题 → roast (短小吐槽)
    // - 连错 4 题 → warn (重警告, 提醒回错题本)
    // - 答错打断长连对 (correctStreak ≥ 3 → 0) → cheer (再起鼓励, 不上头)
    const prevCorrectStreak = get().correctStreak
    const prevWrongStreak = get().wrongStreak
    const newCorrectStreak = result.isCorrect ? prevCorrectStreak + 1 : 0
    const newWrongStreak = result.isCorrect ? 0 : prevWrongStreak + 1

    let nextDanmaku: Danmaku | null = null
    if (result.isCorrect) {
      // 只在正好达到阈值时触发, 避免连对 6/7/8 题连续刷屏
      if (newCorrectStreak === 3) {
        nextDanmaku = pickDanmakuSync(get().danmaku, 'streak_correct_3')
      } else if (newCorrectStreak === 5) {
        nextDanmaku = pickDanmakuSync(get().danmaku, 'streak_correct_5')
      }
    } else {
      // 答错: 优先给鼓励 (打断长连对时不应再骂), 否则按 wrong streak 触发吐槽/警告
      if (prevCorrectStreak >= 3) {
        nextDanmaku = pickDanmakuSync(get().danmaku, 'streak_broken')
      } else if (newWrongStreak === 2) {
        nextDanmaku = pickDanmakuSync(get().danmaku, 'streak_wrong_2')
      } else if (newWrongStreak === 4) {
        nextDanmaku = pickDanmakuSync(get().danmaku, 'streak_wrong_4')
      }
    }

    set({
      correctStreak: newCorrectStreak,
      wrongStreak: newWrongStreak,
      pendingDanmaku: nextDanmaku,
      danmakuTick: get().danmakuTick + 1,
    })
  },

  getProgress: () => {
    return get().deckManager.getProgress()
  },

  getStatistics: () => {
    return get().progressTracker.getStatistics()
  },

  getBehaviorPatterns: () => {
    return get().statisticsEngine.getBehaviorPatterns()
  },

  recordAchievementUnlock: (id: string) => {
    const current = get().unlockedAchievements
    if (current[id] && current[id] > 0) return   // 已解锁, 跳过
    set({ unlockedAchievements: { ...current, [id]: Date.now() } })
  },

  clearLastAnswer: () => {
    set({ lastAnswerResult: null })
  },

  // ========================
  // 答案之书 actions
  // ========================
  loadOracleQuotes: async () => {
    try {
      const { loadOracleQuotes } = await import('../data/oracleLoader')
      const quotes = await loadOracleQuotes()
      set({ oracleQuotes: quotes })
    } catch (err) {
      console.error('Failed to load oracle quotes:', err)
    }
  },

  recordOracleDraw: (quoteId: string) => {
    const cur = get().oracleStorage
    if (cur.drawnIds.includes(quoteId)) return
    const updated: OracleStorage = {
      ...cur,
      drawnIds: [...cur.drawnIds, quoteId],
    }
    set({ oracleStorage: updated })
    persistOracle(updated)
  },

  toggleOracleFavorite: (quoteId: string) => {
    const cur = get().oracleStorage
    const isFav = cur.favorites.includes(quoteId)
    const updated: OracleStorage = {
      ...cur,
      favorites: isFav
        ? cur.favorites.filter(id => id !== quoteId)
        : [...cur.favorites, quoteId],
    }
    set({ oracleStorage: updated })
    persistOracle(updated)
  },

  // ========================
  // 茶馆 actions (留言板升级, 数据 100% 存浏览器 IndexedDB)
  // ========================
  addGuestBookEntry: (content: string, tag: GuestBookTag, mood?: TeahouseMood) => {
    const trimmed = content.trim()
    if (!trimmed) return
    const entry: GuestBookEntry = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
      content: trimmed,
      tag,
      ...(mood ? { mood } : {}),
      createdAt: Date.now(),
    }
    // unshift 保证最新在前 (时间倒序)
    set({ guestBookEntries: [entry, ...get().guestBookEntries] })
    persistGuestBook()
  },

  removeGuestBookEntry: (id: string) => {
    set({ guestBookEntries: get().guestBookEntries.filter(e => e.id !== id) })
    persistGuestBook()
  },

  removeMistake: (cardId: string) => {
    get().mistakeBook.removeMistake(cardId)
    set({ mistakeEntries: get().mistakeBook.getRanked() })
    // 持久化
    persistMistakeBook()
  },

  recordStudyLocation: (chapterId: number, sectionId: string) => {
    set({ lastStudyLocation: { chapterId, sectionId } })
  },

  consumePendingUnlocks: () => {
    const ids = get().pendingUnlockIds
    set({ pendingUnlockIds: [] })
    return ids
  },

  // 解锁全部章节 (把所有卡片 mastery 设为 60, 触发章节解锁阈值)
  unlockAllChapters: () => {
    const { progressTracker } = get()
    for (const chapter of chapters) {
      for (const section of chapter.sections) {
        for (const cardId of section.cardIds) {
          progressTracker.setMastery(cardId, 60)
        }
      }
    }
    // 持久化: 全量存档, 必须包含 oracle 等其他字段避免覆盖
    saveGame({
      progress: progressTracker.toSaveData(),
      statistics: progressTracker.getStatistics(),
      settings: get().settings,
      achievements: Object.entries(get().unlockedAchievements).map(([id, unlockedAt]) => ({ id, unlockedAt })),
      mistakeBook: get().mistakeBook.toSaveData(),
      oracle: get().oracleStorage,
      guestBook: get().guestBookEntries,
      lastStudyLocation: get().lastStudyLocation,
    }).catch(console.error)
    // 递增版本号触发 UI 重新渲染
    set({ unlockVersion: get().unlockVersion + 1 })
  },

  // 清空学习数据 —— 弱重置：进度/勋章/错题/统计/上次位置; 保留 oracle / 茶馆留言 / 设置
  clearLearning: () => {
    const { progressTracker, statisticsEngine, mistakeBook } = get()
    progressTracker.reset()
    statisticsEngine.resetSession()
    mistakeBook.reset()
    set({
      unlockedAchievements: {},
      pendingUnlockIds: [],
      lastStudyLocation: undefined,
      mistakeEntries: [],
      unlockVersion: get().unlockVersion + 1,
    })
    void (async () => {
      try {
        const current = await loadGame()
        await saveGame({
          ...current,
          progress: {},
          statistics: progressTracker.getStatistics(),
          achievements: [],
          mistakeBook: {},
          lastStudyLocation: undefined,
        })
      } catch (err) {
        console.error('clearLearning persist failed:', err)
      }
    })()
  },

  // 全部重置 —— 强重置: clearLearning + 清空 oracle + 清空茶馆留言; 仅保留 settings
  resetAll: () => {
    const { progressTracker, statisticsEngine, mistakeBook } = get()
    progressTracker.reset()
    statisticsEngine.resetSession()
    mistakeBook.reset()
    const freshOracle = { drawnIds: [] as string[], favorites: [] as string[] }
    set({
      unlockedAchievements: {},
      pendingUnlockIds: [],
      lastStudyLocation: undefined,
      mistakeEntries: [],
      guestBookEntries: [],
      oracleStorage: freshOracle,
      unlockVersion: get().unlockVersion + 1,
    })
    saveGame({
      progress: {},
      statistics: progressTracker.getStatistics(),
      settings: get().settings,
      achievements: [],
      mistakeBook: {},
      guestBook: [],
      oracle: freshOracle,
      lastStudyLocation: undefined,
    }).catch(err => console.error('resetAll persist failed:', err))
  },

  // ========================
  // 弹幕 actions
  // ========================
  loadDanmaku: async () => {
    try {
      const { loadDanmaku: fetchDanmaku } = await import('../data/danmakuLoader')
      const items = await fetchDanmaku()
      set({ danmaku: items })
    } catch (err) {
      console.error('loadDanmaku failed:', err)
    }
  },

  consumePendingDanmaku: () => {
    set({ pendingDanmaku: null })
  },
}))

/**
 * 同步版 pickDanmaku —— store submitAnswer 在同步路径里调用
 * 拿不到 pool 时返回 null, 调用方降级 (不显示弹幕即可, 不影响答题主流程)
 */
function pickDanmakuSync(
  pool: Danmaku[],
  trigger: Danmaku['trigger']
): Danmaku | null {
  const matches = pool.filter(d => d.trigger === trigger)
  if (matches.length === 0) return null
  return matches[Math.floor(Math.random() * matches.length)]!
}

/**
 * 把 oracle 状态 + 其他进度一起保存
 * 抽卡保存要全量保存避免覆盖
 */
async function persistOracle(oracle: OracleStorage) {
  try {
    const { loadGame } = await import('../storage/db')
    const current = await loadGame()
    await import('../storage/db').then(m => m.saveGame({
      ...current,
      oracle,
    }))
  } catch (err) {
    console.error('persistOracle failed:', err)
  }
}

/**
 * 错题本持久化
 */
async function persistMistakeBook() {
  try {
    const { loadGame } = await import('../storage/db')
    const { mistakeBook } = useGameStore.getState()
    const current = await loadGame()
    await import('../storage/db').then(m => m.saveGame({
      ...current,
      mistakeBook: mistakeBook.toSaveData(),
    }))
  } catch (err) {
    console.error('persistMistakeBook failed:', err)
  }
}

/**
 * 留言板持久化 (read-modify-write, 全量覆盖前先读, 避免冲掉其他存档字段)
 */
async function persistGuestBook() {
  try {
    const { loadGame } = await import('../storage/db')
    const { guestBookEntries } = useGameStore.getState()
    const current = await loadGame()
    await import('../storage/db').then(m => m.saveGame({
      ...current,
      guestBook: guestBookEntries,
    }))
  } catch (err) {
    console.error('persistGuestBook failed:', err)
  }
}
